<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';

const props = defineProps<{
  modelValue: any;
  options: { label: string; value: any; disabled?: boolean }[] | string[];
  disabled?: boolean;
  placeholder?: string;
  scrollIntoViewOnOpen?: boolean;
  inlineDropdown?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
}>();

const isOpen = ref(false);
const selectRef = ref<HTMLElement | null>(null);



const formattedOptions = computed(() => {
  return props.options.map(opt => {
    if (typeof opt === 'string') {
      return { label: opt, value: opt };
    }
    return opt;
  });
});

const selectedLabel = computed(() => {
  const selected = formattedOptions.value.find(o => o.value === props.modelValue);
  return selected ? selected.label : props.placeholder || 'Select...';
});

// With at most one selectable option, opening the dropdown only ever shows the one thing already
// picked (plus other options greyed out and unpickable) — nothing a buyer can actually choose. The
// trigger still displays the selected value; it just stops acting like something that opens.
const selectableCount = computed(() => formattedOptions.value.filter(o => !o.disabled).length);
const canOpen = computed(() => !props.disabled && selectableCount.value > 1);

function selectOption(option: { value: any; disabled?: boolean }) {
  if (option.disabled) return;
  emit('update:modelValue', option.value);
  setTimeout(() => {
    isOpen.value = false;
  }, 50);
}

function onWindowClick(e: MouseEvent) {
  if (!isOpen.value) return;
  if (selectRef.value && !selectRef.value.contains(e.target as Node)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  window.addEventListener('click', onWindowClick, { capture: true });
});
onUnmounted(() => {
  window.removeEventListener('click', onWindowClick, { capture: true });
});

function toggleOpen() {
  if (!canOpen.value) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value && props.scrollIntoViewOnOpen) {
    setTimeout(() => {
      if (!selectRef.value) return;
      const dropdown = selectRef.value.querySelector('.z-50');
      if (dropdown) {
        dropdown.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else {
        selectRef.value.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50); // Small delay to let the transition start and display:block take effect
  }
}
</script>

<template>
  <div class="relative w-full" ref="selectRef">
    <!-- Trigger Button -->
    <button
      type="button"
      :disabled="disabled"
      @click.stop="toggleOpen"
      :class="[
        'flex w-full items-center justify-between gap-2 rounded-[6px] border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        canOpen ? 'cursor-pointer' : 'cursor-default',
        isOpen
          ? 'border-on-primary-heading bg-on-primary-heading text-white'
          : ['border-button-secondary-hover bg-button-secondary text-on-primary-heading', canOpen && 'hover:bg-button-secondary-hover']
      ]"
    >
      <span class="truncate">{{ selectedLabel }}</span>
      <Icon v-if="canOpen" :name="isOpen ? 'lucide:chevron-up' : 'lucide:chevron-down'" class="size-3.5 shrink-0" />
    </button>

    <!-- Dropdown Panel (Local) -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="isOpen && canOpen"
        :class="[
          inlineDropdown ? 'relative mt-1' : 'absolute left-0 right-0 top-[calc(100%+4px)]',
          'z-50 w-full rounded-[6px] border border-on-primary-border bg-white p-2 shadow-lg flex flex-col gap-1 max-h-60 overflow-y-auto'
        ]"
      >
        <button
          v-for="option in formattedOptions"
          :key="option.value"
          type="button"
          :disabled="option.disabled"
          @click.stop.prevent="selectOption(option)"
          :class="[
            'flex w-full items-center justify-between rounded-[4px] px-3 py-2 text-sm font-medium transition-colors hover:bg-button-secondary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent',
            modelValue === option.value 
              ? 'bg-button-secondary text-on-primary-heading' 
              : (option.disabled ? 'text-gray-400' : 'text-on-primary-body hover:text-on-primary-heading')
          ]"
        >
          <span class="truncate text-left">{{ option.label }}</span>
          <Icon v-if="modelValue === option.value" name="lucide:check" class="size-3.5 shrink-0 ml-2" />
        </button>
      </div>
    </Transition>
  </div>
</template>
